import React, { useState, useEffect } from 'react';
import Modal from '../components/Modal';
import { customerService } from '../services/customerService';
import { Customer, CustomerFormData } from '../types/types';
import { UserCircle, Plus, Edit2, Trash2, Search, Mail, Phone, MapPin } from 'lucide-react';
import './CustomerManagement.css';

const CustomerManagement: React.FC = () => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [formData, setFormData] = useState<CustomerFormData>({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
    });

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        setLoading(true);
        try {
            const data = await customerService.getAllCustomers();
            setCustomers(data);
        } catch (error) {
            console.error('Error loading customers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setFormData({
            name: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
        });
        setCreateModalOpen(true);
    };

    const handleEdit = (customer: Customer) => {
        setSelectedCustomer(customer);
        setFormData({
            name: customer.name,
            lastName: customer.lastName,
            email: customer.email || '',
            phone: customer.phone || '',
            address: customer.address || '',
        });
        setEditModalOpen(true);
    };

    const handleDelete = (customer: Customer) => {
        setSelectedCustomer(customer);
        setDeleteModalOpen(true);
    };

    const confirmCreate = async () => {
        try {
            await customerService.createCustomer(formData);
            await loadCustomers();
            setCreateModalOpen(false);
        } catch (error) {
            console.error('Error creating customer:', error);
            alert('Error al crear el cliente');
        }
    };

    const confirmEdit = async () => {
        if (!selectedCustomer) return;

        try {
            await customerService.updateCustomer(selectedCustomer.id, formData);
            await loadCustomers();
            setEditModalOpen(false);
            setSelectedCustomer(null);
        } catch (error) {
            console.error('Error updating customer:', error);
            alert('Error al actualizar el cliente');
        }
    };

    const confirmDelete = async () => {
        if (!selectedCustomer) return;

        try {
            await customerService.deleteCustomer(selectedCustomer.id);
            await loadCustomers();
            setDeleteModalOpen(false);
            setSelectedCustomer(null);
        } catch (error) {
            console.error('Error deleting customer:', error);
            alert('Error al eliminar el cliente');
        }
    };

    const filteredCustomers = customers.filter(
        (customer) =>
            customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="customer-management-container">
                <div className="page-header">
                    <div>
                        <h1>Gestión de Clientes</h1>
                        <p className="page-subtitle">Administrar clientes registrados</p>
                    </div>
                    <UserCircle size={32} className="header-icon" />
                </div>

                <div className="card">
                    <div className="table-controls">
                        <div className="search-box">
                            <Search size={20} />
                            <input
                                type="text"
                                placeholder="Buscar clientes..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <button className="btn btn-primary" onClick={handleCreate}>
                            <Plus size={20} />
                            Nuevo Cliente
                        </button>
                    </div>

                    {loading ? (
                        <div className="loading-state">Cargando clientes...</div>
                    ) : (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Nombre</th>
                                        <th>Apellido</th>
                                        <th>Email</th>
                                        <th>Teléfono</th>
                                        <th>Dirección</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCustomers.map((customer) => (
                                        <tr key={customer.id}>
                                            <td>{customer.id}</td>
                                            <td>
                                                <div className="customer-cell">
                                                    <span className="customer-name">{customer.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="customer-cell">
                                                    <span className="customer-name">{customer.lastName}</span>
                                                </div>
                                            </td>
                                            <td>
                                                {customer.email ? (
                                                    <div className="contact-info">
                                                        <Mail size={14} />
                                                        <span>{customer.email}</span>
                                                    </div>
                                                ) : (
                                                    <span className="no-data">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {customer.phone ? (
                                                    <div className="contact-info">
                                                        <Phone size={14} />
                                                        <span>{customer.phone}</span>
                                                    </div>
                                                ) : (
                                                    <span className="no-data">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {customer.address ? (
                                                    <div className="contact-info">
                                                        <MapPin size={14} />
                                                        <span>{customer.address}</span>
                                                    </div>
                                                ) : (
                                                    <span className="no-data">-</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn-icon btn-edit"
                                                        onClick={() => handleEdit(customer)}
                                                        title="Editar cliente"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        className="btn-icon btn-delete"
                                                        onClick={() => handleDelete(customer)}
                                                        title="Eliminar cliente"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredCustomers.length === 0 && (
                                <div className="empty-state">No se encontraron clientes</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Create Modal */}
                <Modal
                    isOpen={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                    title="Crear Nuevo Cliente"
                    size="medium"
                >
                    <CustomerForm
                        formData={formData}
                        setFormData={setFormData}
                        onSubmit={confirmCreate}
                        onCancel={() => setCreateModalOpen(false)}
                    />
                </Modal>

                {/* Edit Modal */}
                <Modal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    title="Editar Cliente"
                    size="medium"
                >
                    <CustomerForm
                        formData={formData}
                        setFormData={setFormData}
                        onSubmit={confirmEdit}
                        onCancel={() => setEditModalOpen(false)}
                    />
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    title="Confirmar Eliminación"
                    size="small"
                >
                    <div className="delete-confirmation">
                        <p>
                            ¿Está seguro que desea eliminar el cliente{' '}
                            <strong>{selectedCustomer?.name}</strong>?
                        </p>
                        <p className="warning-text">Esta acción no se puede deshacer.</p>

                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteModalOpen(false)}>
                                Cancelar
                            </button>
                            <button className="btn btn-danger" onClick={confirmDelete}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
    );
};

// Customer Form Component
interface CustomerFormProps {
    formData: CustomerFormData;
    setFormData: React.Dispatch<React.SetStateAction<CustomerFormData>>;
    onSubmit: () => void;
    onCancel: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
    formData,
    setFormData,
    onSubmit,
    onCancel,
}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    return (
        <div className="modal-form">
            <div className="form-group">
                <label htmlFor="name">Nombre *</label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label htmlFor="lastName">Apellido *</label>
                <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label htmlFor="address">Dirección</label>
                <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows={3}
                    className="form-input"
                />
            </div>

            <div className="modal-actions">
                <button className="btn btn-outline" onClick={onCancel}>
                    Cancelar
                </button>
                <button className="btn btn-primary" onClick={onSubmit}>
                    Guardar
                </button>
            </div>
        </div>
    );
};

export default CustomerManagement;
